import { useState, useEffect } from 'react';
import { useFetchByKey } from './useFetchByKey.hook';
import type { NodeToRenderProps } from '@src/components/custom-nodes/types/custom-nodes.type';

// Define the deletion predicate types to match custom-nodes.type.ts
export interface DeletionPredicate {
  fromFetchContext: boolean;
  predicate: 'includes' | 'equals' | 'notIncludes' | 'notEquals';
  fetchContextFunction: string;
  key: string;
  valuePath: string;
  violationMessage: string;
}

/**
 * Hook to determine if a node can be deleted based on a deletion predicate
 *
 * @param selectedNodeProps The node properties to check against
 * @param deletionPredicate The predicate configuration to check for deletion permission
 * @returns [boolean, boolean] - [allowDeletion, isLoading]
 */
export const useAllowDeletion = (selectedNodeProps: NodeToRenderProps, deletionPredicate?: DeletionPredicate): [boolean, boolean, string] => {
  const [allowDeletion, setAllowDeletion] = useState<boolean>(true);

  // Always call hooks at the top level regardless of conditions
  const fetchKey = deletionPredicate?.fetchContextFunction ?? '';
  const valuePath = deletionPredicate?.valuePath ?? '';

  const result = useFetchByKey(fetchKey, valuePath, {
    ...selectedNodeProps,
  });
  const predicateData = result[0] as unknown[];
  const isLoadingPredicateData = result[1] as boolean;

  useEffect(() => {
    // If no deletion predicate is provided, deletion is always allowed
    if (!deletionPredicate || isLoadingPredicateData || !selectedNodeProps?.id) {
      return;
    }

    if (!Array.isArray(predicateData)) {
      // If data is not an array, default to allowing deletion
      return;
    }

    const valueToCompare = selectedNodeProps?.[deletionPredicate?.key as keyof NodeToRenderProps] as unknown;
    const hasValueToCompare = valueToCompare !== undefined;

    if (predicateData.length === 0) {
      setAllowDeletion(true);
      return;
    }

    // Handle different predicate types
    switch (deletionPredicate.predicate) {
      case 'includes':
        setAllowDeletion(hasValueToCompare && predicateData.includes(valueToCompare));
        break;
      case 'notIncludes':
        setAllowDeletion(hasValueToCompare && !predicateData.includes(valueToCompare));
        break;
      case 'equals':
        // Compare first item with all other items for equality
        setAllowDeletion(hasValueToCompare && predicateData.every(item => item === valueToCompare));
        break;
      case 'notEquals':
        // Check if any item is not equal to the first item
        setAllowDeletion(hasValueToCompare && predicateData.some(item => item !== valueToCompare));
        break;
      default:
        // Default to allowing deletion
        setAllowDeletion(true);
    }
  }, [isLoadingPredicateData, predicateData, deletionPredicate, selectedNodeProps?.id]);

  return [allowDeletion, isLoadingPredicateData, selectedNodeProps?.id];
};
